import { useState } from "react";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";
import { trackEvent } from "../../utils/pixels";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({
    navn: "",
    email: "",
    telefon: "",
    lokation: "",
    besked: "",
    samtykke: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);

    const accessKey =
      process.env.REACT_APP_WEB3FORMS_KEY || "YOUR_ACCESS_KEY_HERE";

    console.log(
      "[Contact Form] Access key:",
      accessKey.substring(0, 8) + "..."
    );
    console.log("[Contact Form] Environment:", process.env.NODE_ENV);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: accessKey,
          name: form.navn,
          email: form.email,
          phone: form.telefon,
          location: form.lokation,
          message: form.besked,
          subject: "Ny besked fra Plejesøstrene kontaktformular",
          from_name: "Plejesøstrene Website",
          to_email: "ot@plejesoestrene.dk",
        }),
      });

      const data = await response.json();
      console.log("[Contact Form] Response:", data);

      if (data.success) {
        let userData = null;
        try {
          const consent = JSON.parse(
            localStorage.getItem("cookie_consent") || "{}"
          );
          if (consent.marketing) {
            userData = {
              email: form.email,
              phone: form.telefon,
              firstName: form.navn.split(" ")[0],
              lastName:
                form.navn.split(" ").slice(1).join(" ") || undefined,
            };
          }
        } catch (err) {
          // Ignore consent parse errors
        }
        trackEvent("Lead", {}, userData);
        setSent(true);
        setForm({
          navn: "",
          email: "",
          telefon: "",
          lokation: "",
          besked: "",
          samtykke: false,
        });
      } else {
        setError(true);
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="section py-10 sm:py-14 md:py-18 bg-white">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="kicker">Kontakt</span>
          <h2 className="section-title mt-2">Lad os tage en snak</h2>
          <p className="section-subtitle mx-auto">
            Uanset om I søger vikardækning eller ønsker at blive vikar – vi er
            klar til en uforpligtende dialog om jeres behov.
          </p>
        </div>

        {sent ? (
          <div className="mt-10 card-elevated p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="text-emerald-500" size={28} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">
              Tak for din besked!
            </h3>
            <p className="mt-2 text-slate-500 text-sm">
              Vi vender tilbage inden for 24 timer.
            </p>
            <button
              onClick={() => setSent(false)}
              className="mt-6 text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline transition-colors"
            >
              Send en ny besked
            </button>
          </div>
        ) : (
          <div className="mt-10">
            {error && (
              <div className="mb-6 p-4 rounded-card bg-red-50 border border-red-200 flex items-start gap-3">
                <AlertCircle
                  className="text-red-500 shrink-0 mt-0.5"
                  size={18}
                />
                <p className="text-sm text-red-700">
                  Der opstod en fejl. Prøv venligst igen eller kontakt os
                  direkte på{" "}
                  <a
                    href="mailto:ot@plejesoestrene.dk"
                    className="font-semibold underline"
                  >
                    ot@plejesoestrene.dk
                  </a>
                </p>
              </div>
            )}

            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="grid gap-1.5">
                  <span className="text-sm font-medium text-slate-700">
                    Navn
                  </span>
                  <input
                    className="input"
                    value={form.navn}
                    onChange={(e) =>
                      setForm({ ...form, navn: e.target.value })
                    }
                    placeholder="Dit fulde navn"
                    required
                  />
                </label>
                <label className="grid gap-1.5">
                  <span className="text-sm font-medium text-slate-700">
                    E-mail
                  </span>
                  <input
                    className="input"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    placeholder="dig@eksempel.dk"
                    required
                  />
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <label className="grid gap-1.5">
                  <span className="text-sm font-medium text-slate-700">
                    Telefonnummer
                  </span>
                  <input
                    className="input"
                    type="tel"
                    value={form.telefon}
                    onChange={(e) =>
                      setForm({ ...form, telefon: e.target.value })
                    }
                    placeholder="12 34 56 78"
                  />
                </label>
                <label className="grid gap-1.5">
                  <span className="text-sm font-medium text-slate-700">
                    Lokation
                  </span>
                  <select
                    className="input"
                    value={form.lokation}
                    onChange={(e) =>
                      setForm({ ...form, lokation: e.target.value })
                    }
                  >
                    <option value="" disabled>
                      Vælg lokation
                    </option>
                    <optgroup label="Fyn">
                      <option value="Odense">Odense</option>
                      <option value="Hele Fyn">Hele Fyn</option>
                    </optgroup>
                    <option value="Langeland">Langeland</option>
                    <optgroup label="Trekantsområdet">
                      <option value="Kolding">Kolding</option>
                      <option value="Haderslev">Haderslev</option>
                      <option value="Hele trekantsområdet">
                        Hele trekantsområdet
                      </option>
                    </optgroup>
                    <option value="Andet">Andet</option>
                  </select>
                </label>
              </div>

              <label className="grid gap-1.5">
                <span className="text-sm font-medium text-slate-700">
                  Besked
                </span>
                <textarea
                  className="input"
                  rows={5}
                  value={form.besked}
                  onChange={(e) =>
                    setForm({ ...form, besked: e.target.value })
                  }
                  placeholder="Skriv kort om dit behov eller din ansøgning..."
                  required
                />
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  required
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 focus:ring-offset-0 shrink-0"
                  checked={form.samtykke}
                  onChange={(e) =>
                    setForm({ ...form, samtykke: e.target.checked })
                  }
                />
                <span className="text-sm text-slate-500 leading-relaxed group-hover:text-slate-600 transition-colors">
                  Jeg accepterer at Plejesøstrene ApS behandler mine
                  personoplysninger i henhold til{" "}
                  <a
                    href="/privatlivspolitik"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-brand-600 hover:underline"
                  >
                    privatlivspolitikken
                  </a>
                  . Mine data bruges kun til at besvare min henvendelse.
                </span>
              </label>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-slate-400 order-2 sm:order-1">
                  Læs mere i vores{" "}
                  <a
                    href="/privatlivspolitik"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline"
                  >
                    privatlivspolitik
                  </a>
                  .
                </p>
                <button
                  className="btn btn-primary btn-lg w-full sm:w-auto order-1 sm:order-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sender...
                    </>
                  ) : (
                    <>
                      Send besked
                      <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
